import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Paper,
    Divider
} from "@mui/material";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where,
    Timestamp
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import Menu from "../../panel/Menu";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* --- METICULOUS PRINT STYLES --- */
  @media print {
    @page {
        size: A4;
        margin: 5mm;
    }

    html, body, #root {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        color: #000 !important;
    }

    .no-print, nav, header, footer, .sidebar, button, .MuiTextField-root {
        display: none !important;
    }

    * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        text-shadow: none !important;
        animation: none !important;
        border-color: #ccc !important;
    }

    .page-fade-in {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .print-area {
        border: none !important;
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
    }

    table {
        width: 100% !important;
        border-collapse: collapse !important;
    }
    
    th, td {
        border: 1px solid #ddd !important;
        padding: 6px 8px !important;
        color: #000 !important;
    }

    th {
        background-color: #f5f5f5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        font-weight: bold !important;
    }
  }
`;

function Datewise() {
    const printRef = useRef();

    const [propertyId, setPropertyId] = useState("");
    const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
    const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
    const [bookingData, setBookingData] = useState([]);

    /* ================= Toast ================= */
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        background: colors.calPolyGreen,
        color: colors.vanilla
    });

    /* ================= AUTH ================= */
    useEffect(() => {
        auth.onAuthStateChanged(async (user) => {
            if (!user) return;
            const roleSnap = await getDoc(doc(db, "authroles", user.uid));
            if (roleSnap.exists()) {
                setPropertyId(roleSnap.data().propertyId);
            }
        });
    }, []);

    useEffect(() => {
        if (propertyId && fromDate && toDate) {
            getReport();
        }
    }, [propertyId]);

    /* ================= GET REPORT ================= */
    const getReport = async () => {
        if (!fromDate || !toDate || !propertyId) {
            Toast.fire({ icon: "error", title: "Select dates" });
            return;
        }

        setBookingData([]);

        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(toDate);
        end.setDate(end.getDate() + 1);

        try {
            /* === PAYMENTS === */
            const paySnap = await getDocs(
                query(
                    collection(db, "payhistory"),
                    where("propertyId", "==", propertyId),
                    where("status", "==", "Working"),
                    where("createdAt", ">=", Timestamp.fromDate(start)),
                    where("createdAt", "<", Timestamp.fromDate(end))
                )
            );

            const paymentMap = {};
            paySnap.forEach(d => {
                const data = d.data();
                if (!paymentMap[data.bookingid]) paymentMap[data.bookingid] = [];
                paymentMap[data.bookingid].push(data);
            });

            /* === CHECKINS === */
            const checkinSnap = await getDocs(
                query(
                    collection(db, "checkindetails"),
                    where("propertyId", "==", propertyId),
                    where("status", "in", ["Booked", "Checkout"]),
                    where("checkindate", ">=", fromDate),
                    where("checkindate", "<=", toDate)
                )
            );

            const checkins = checkinSnap.docs.map(d => d.data());
            const bookingIds = [...new Set(checkins.map(c => c.bookingid))];

            if (!bookingIds.length) {
                Toast.fire({ icon: "info", title: "No records found for this date range" });
                return;
            }

            /* === RESERVATIONS === */
            const bookingMap = {};
            const tempBookingIds = [...bookingIds];
            while (tempBookingIds.length) {
                const chunk = tempBookingIds.splice(0, 30);
                const snap = await getDocs(
                    query(collection(db, "reservations"), where("__name__", "in", chunk))
                );
                snap.forEach(d => (bookingMap[d.id] = d.data()));
            }

            /* === FINAL === */
            const final = [];

            for (const row of checkins) {
                const booking = bookingMap[row.bookingid];
                if (!booking) continue;

                const payments = paymentMap[row.bookingid] || [];
                const paid = payments.reduce(
                    (s, p) => s + (p.amount1 || 0) + (p.amount2 || 0),
                    0
                );

                const room = Number(booking.rooamount || 0);
                const service =
                    Number(booking.serviceamount || 0) /
                    Number(booking.noofdays || 1);

                const total = room + service;

                final.push({
                    date: row.checkindate,
                    customer: booking.customers?.custname || booking.custname || "Unknown",
                    username: row.username || "Admin",
                    room: room.toFixed(2),
                    service: service.toFixed(2),
                    total: total.toFixed(2),
                    paid: paid.toFixed(2),
                    pending: (total - paid).toFixed(2)
                });
            }

            setBookingData(final);
        } catch (error) {
            console.error(error);
            Toast.fire({ icon: "error", title: "Failed to fetch report" });
        }
    };

    /* ================= GROUP BY DATE ================= */
    const grouped = bookingData.reduce((acc, r) => {
        if (!acc[r.date]) acc[r.date] = [];
        acc[r.date].push(r);
        return acc;
    }, {});

    /* ================= PRINT ================= */
    const handlePrint = () => {
        window.print();
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }} className="page-fade-in">
            <style>{animationStyles}</style>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>

                <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }} className="no-print">
                    Datewise Booking Report
                </Typography>

                {/* FILTER */}
                <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }} className="no-print">
                    <CardContent sx={{ p: 3 }}>
                        <div className="container-fluid p-0">
                            <div className="row align-items-center">
                                <div className="col-sm-4 mb-3 mb-sm-0">
                                    <TextField
                                        type="date"
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-4 mb-3 mb-sm-0">
                                    <TextField
                                        type="date"
                                        label="To Date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-4">
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={getReport}
                                        sx={{ 
                                            bgcolor: colors.goldenrod, 
                                            color: colors.darkGreen, 
                                            fontWeight: 700, 
                                            textTransform: "none", 
                                            borderRadius: "8px",
                                            height: "40px",
                                            '&:hover': { bgcolor: colors.gold } 
                                        }}
                                    >
                                        Get Report
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* REPORT */}
                <Card className="print-area" ref={printRef} sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>
                        {Object.entries(grouped).map(([date, rows]) => {
                            const subtotal = rows.reduce((s, r) => s + Number(r.total), 0);

                            return (
                                <Box key={date} mb={4}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight="bold"
                                        sx={{ mb: 1.5, color: colors.gold }}
                                    >
                                        Date : {moment(date).format("DD-MM-YYYY")}
                                    </Typography>

                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            overflowX: "hidden",
                                            borderRadius: "8px",
                                            border: `1px solid ${colors.goldenrod}`,
                                            backgroundColor: "transparent"
                                        }}
                                    >
                                        <Table
                                            size="small"
                                            sx={{
                                                tableLayout: "fixed",
                                                width: "100%"
                                            }}
                                        >
                                            <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                                <TableRow>
                                                    <TableCell sx={{ width: "8%", color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                                                    <TableCell sx={{ width: "42%", color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Customer</TableCell>
                                                    <TableCell align="right" sx={{ width: "15%", color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>
                                                        Room
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: "15%", color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>
                                                        Service
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ width: "20%", color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>
                                                        Total
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {rows.map((r, i) => (
                                                    <TableRow key={i} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                                        <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{i + 1}</TableCell>

                                                        <TableCell
                                                            sx={{
                                                                wordBreak: "break-word",
                                                                whiteSpace: "normal",
                                                                color: colors.gold,
                                                                fontWeight: 600,
                                                                borderBottom: `1px solid rgba(222,226,177,0.1)`
                                                            }}
                                                        >
                                                            {r.customer}
                                                        </TableCell>

                                                        <TableCell align="right" sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.room}</TableCell>
                                                        <TableCell align="right" sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.service}</TableCell>
                                                        <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{r.total}</TableCell>
                                                    </TableRow>
                                                ))}

                                                {/* DATE TOTAL */}
                                                <TableRow sx={{ backgroundColor: colors.darkGreen }}>
                                                    <TableCell colSpan={4} align="right" sx={{ color: colors.gold, fontWeight: 700, borderBottom: 0 }}>
                                                        Date Total
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: colors.gold, fontWeight: 700, borderBottom: 0 }}>
                                                        {subtotal.toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Divider sx={{ mt: 3, borderColor: colors.goldenrod, opacity: 0.2 }} />
                                </Box>
                            );
                        })}

                        {Object.keys(grouped).length === 0 && (
                            <Typography align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                No datewise bookings found
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                <div className="text-center mt-3 no-print">
                    <Button 
                        variant="contained" 
                        onClick={handlePrint}
                        sx={{ 
                            bgcolor: colors.goldenrod, 
                            color: colors.darkGreen, 
                            fontWeight: 700, 
                            textTransform: "none", 
                            borderRadius: "8px",
                            px: 4,
                            py: 1.2,
                            '&:hover': { bgcolor: colors.gold } 
                        }}
                    >
                        Print Report
                    </Button>
                </div>
            </Box>
        </Box>
    );
}

export default Datewise;